/*--------------------------------------------------------
 * Author Trần Đức Tiến
 * Email ductienas@gmail.com
 * Phone 0972970075
 * Created: 2017-05-23 11:20:38
 *
 * LastModified: 2017-05-23 11:20:38
 *-------------------------------------------------------*/

import { take, call, put, cancel, fork, all } from 'redux-saga/effects';
import fetchApi, { uploadImage } from '../../../utils/fetchApi';
import Configs from '../../../constants/Config';

function* authorize(email, password, next) {
  try {
    const response = yield call(fetchApi, 'Accounts/login', { email, password }, { method: 'POST' });

    if (response && !response.error) {
      yield put({
        type: 'LOGIN_SUCCESS',
        payload: response,
      });
      if (next && typeof next === 'function') {
        next(response.account);
      }
    }
  } catch (err) {
    yield put({ type: 'REQUEST_ERROR', payload: err });
  }
}

function* loginFlow() {
  const INFINITE = true;

  while (INFINITE) {
    const { payload: { email, password }, next } = yield take('LOGIN_REQUEST');
    const authorizeTask = yield fork(authorize, email, password, next);
    const action = yield take(['LOGOUT_REQUEST', 'LOGIN_FAILED', 'REQUEST_ERROR']);

    if (action.type === 'LOGOUT_REQUEST') {
      yield cancel(authorizeTask);
    }
  }
}

function* logoutFlow() {
  const INFINITE = true;

  while (INFINITE) {
    const { next } = yield take('LOGOUT_REQUEST');
    try {
      const response = yield call(fetchApi, 'Accounts/logout', {}, { method: 'POST' });
      if (response && !response.error) {
        yield put({ type: 'REMOVE_AUTH' });
        if (next && typeof next === 'function') {
          next();
        }
      }
    } catch (err) {
      yield put({ type: 'REQUEST_ERROR', payload: err });
    }
  }
}

function* resetPasswordRequestFlow() {
  const INFINITE = true;

  while (INFINITE) {
    const { payload: { email }, next } = yield take('REST_PASSWORD_REQUEST');
    try {
      const response = yield call(fetchApi, 'Accounts/reset', { email }, { method: 'POST' });
      if (response && !response.error) {
        if (next && typeof next === 'function') {
          next();
        }
      }
    } catch (err) {
      yield put({ type: 'REQUEST_ERROR', payload: err });
    }
  }
}

function* resetPasswordFlow() {
  const INFINITE = true;

  while (INFINITE) {
    const { payload: { newPassword, accessToken }, next } = yield take('REST_PASSWORD');
    try {
      const response = yield call(fetchApi, 'Accounts/reset-password', { newPassword }, { method: 'POST', headers: { Authorization: accessToken } });
      if (response && !response.error) {
        if (next && typeof next === 'function') {
          next();
        }
      }
    } catch (err) {
      yield put({ type: 'REQUEST_ERROR', payload: err });
    }
  }
}

function* getAuthUserFlow() {
  const INFINITE = true;

  while (INFINITE) {
    const { next } = yield take('GET_AUTH_USER');
    try {
      const response = yield call(fetchApi, `Accounts/currentInfo`)

      if (response && !response.error) {
        yield put({ type: 'SET_AUTH', payload: response });
        if (next && typeof next === 'function') {
          next();
        }
      }
    } catch (err) {
      yield put({ type: 'REQUEST_ERROR', payload: err });
    }
  }
}

function* fetchAuthUserBackgroundFlow() {
  const INFINITE = true;

  while (INFINITE) {
    const { payload: { id }, next } = yield take('FETCH_AUTH_USER_BACKGROUND');
    try {
      const response = yield call(fetchApi, `Accounts/currentInfo`, null, null, true);
      if (response && !response.error) {
        yield put({ type: 'SET_AUTH', payload: response });
        if (next && typeof next === 'function') {
          next();
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.log(err)
      }
    }
  }
}

function* signUpFlow() {
  const INFINITE = true;

  while (INFINITE) {
    const { payload, next } = yield take('SIGN_UP_REQUEST');
    delete payload.confirmPassword;
    try {
      const response = yield call(fetchApi, 'Accounts', payload, { method: 'POST' });
      if (response && !response.error) {
        if (next && typeof next === 'function') {
          next();
        }
      }
    } catch (err) {
      yield put({ type: 'REQUEST_ERROR', payload: err });
    }
  }
}

function* resendVerificationEmailFlow() {
  const INFINITE = true;

  while (INFINITE) {
    const { payload: { email }, next } = yield take('RESEND_VERIFICATION_EMAIL');
    try {
      const response = yield call(fetchApi, 'Accounts/resend-verification-email', { email }, { method: 'POST' });
      if (response && !response.error) {
        if (next && typeof next === 'function') {
          next();
        }
      }
    } catch (err) {
      yield put({ type: 'REQUEST_ERROR', payload: err });
    }
  }
}

function* editProfileFlow() {
  const INFINITE = true;

  while (INFINITE) {
    const { payload, next } = yield take('EDIT_PROFILE');
    try {
      if (payload.filePhoto) {
        const formData = new FormData();
        formData.append('file', payload.filePhoto, 'avatar.png');

        let s3Bucket = Configs.S3_BUCKET;
        // if (Auth.s3Url()) {
        // 	const subDomains = Auth.s3Url().split('.');
        // 	if (subDomains.length > 0) {
        // 		const firstSub = subDomains[0];
        // 		if (firstSub.indexOf('http://') >= 0) {
        // 			s3Bucket = firstSub.split('http://')[1];
        // 		} else if (firstSub.indexOf('https://') >= 0) {
        // 			s3Bucket = firstSub.split('https://')[1];
        // 		}
        // 	}
        // }
        const img = yield call(uploadImage, `attachments/${s3Bucket}/upload`, formData);

        delete payload.photo;
        delete payload.filePhoto;

        if (img && !img.error) {
          payload.photo = img.result.files.file[0].name;
        }
      }

      const userId = payload.accountId;
      const storeId = payload.id;

      delete payload.id;
      delete payload.accountId;
      delete payload.created;

      payload.updated = new Date();
      // payload.editProfile = true;
      if (payload.domainWhitelist) {
        payload.domainWhitelist = payload.domainWhitelist
          .split(',')
          .map(text => text.trim())
          .filter(text => !!text)
          .map(text => {
            if (text.startsWith('http://') || text.startsWith('https://')) {
              return new URL(text).hostname
            }
            return text
          })
          .filter(text => text.split('.').length >= 2)
          .splice(0, 2)
      }

      const response = yield all([
        call(fetchApi, `Accounts/${userId}`, { editProfile: true }, { method: 'PATCH' }),
        call(fetchApi, `Accounts/${userId}/stores/${storeId}`, payload, { method: 'PUT' })
      ]);

      if (response && response[0] && !response[0].error) {
        yield put({
          type: 'EDIT_PROFILE_SUCCESS',
          payload: { ...response[0], store: response[1] }
        });
        if (next && typeof next === 'function') {
          next();
        }
      }
    } catch (err) {
      yield put({ type: 'REQUEST_ERROR', payload: err });
    }
  }
}

export default function* authFlow() {
  yield fork(loginFlow);
  yield fork(logoutFlow);
  yield fork(resetPasswordRequestFlow);
  yield fork(resetPasswordFlow);
  yield fork(getAuthUserFlow);
  yield fork(fetchAuthUserBackgroundFlow);
  yield fork(signUpFlow);
  yield fork(resendVerificationEmailFlow);
  yield fork(editProfileFlow);
}



// WEBPACK FOOTER //
// ./routes/auth/saga/index.js